/**
 * THIN-7802: shared status rule for the DSN hard-delete affordance, so the DSN page
 * and the Episode of Care list cannot drift apart again.
 *
 * SessionNotesStatus (restore-core) is a forward-only lifecycle:
 *   NOT_STARTED -> IN_PROGRESS -> FINALIZED -> SIGNED
 *
 * The requirement from THIN-7637 is that Delete appears "only after the note is
 * submitted (Status='Finalized')" — a *lower bound* on the lifecycle, not an exact
 * match. Testing `status === 'FINALIZED'` accidentally excluded SIGNED, the state
 * that comes straight after it, which is why a signed note offered Delete in the EoC
 * list but not on the DSN page.
 *
 * Signing is explicitly NOT a blocker: per the THIN-7634 grooming decision, "closed
 * month is the only blocker — billed or co-signed does NOT block". The backend agrees
 * — DailySessionNotesServiceV2Impl.deleteDailySessionNote validates the reason, the
 * DELETE_DAILY_NOTES permission, facility scope and the closed-month rule, and never
 * looks at signature state.
 */
export const DELETABLE_DSN_STATUSES = ['FINALIZED', 'SIGNED'] as const;

/**
 * True once a DSN has been submitted (FINALIZED) and for every later state (SIGNED).
 * Anything earlier — NOT_STARTED, IN_PROGRESS, or a missing status — has no submitted
 * note to delete.
 */
export const isDeletableDsnStatus = (status?: string | null): boolean =>
  !!status && (DELETABLE_DSN_STATUSES as readonly string[]).includes(status);
